import React, { useEffect, useState } from 'react';
import SPASelect from '../shared/SPASelect';
import compactSelectStyles from '../shared/compactSelectStyles';
import useFilterOptions from './useFilterOptions';
import type { FilterOption, OptionKind, OptionScope } from './useFilterOptions';

const DEBOUNCE_MS = 300;

// What an empty menu means, per kind — mostly "narrow by a teacher first", which the options need.
const EMPTY: Record<OptionKind, (scoped: boolean) => string> = {
  assignment: scoped => (scoped ? 'No assignment matches' : 'Pick a teacher first, or enter an ID'),
  classroom: scoped => (scoped ? 'No class matches' : 'Pick a teacher or district first, or enter an ID'),
  staffer: () => 'Type a name, email or ID',
  student: scoped => (scoped
    ? 'No student matches'
    : 'Pick a teacher or district to search by name, or enter an ID or username'),
  subscriber: () => 'Type a district name or ID',
};

const OptionLabel = ({ context, option }: { context: 'menu' | 'value'; option: FilterOption }) => (
  <span>
    {option.label}
    {context === 'menu' && option.detail != null && (
      <span className="tw-ml-2 tw-text-sm tw-text-spa-tertiary">{option.detail}</span>
    )}
  </span>
);

interface FilterSelectProps {
  inputId: string;
  kind: OptionKind;
  onChange: (value: string) => void;
  placeholder?: string;
  scope: OptionScope;
  value: string;
}

/** A troubleshoot filter picked by name; the id it stands for is what the filter keeps. */
const FilterSelect = ({
  inputId, kind, onChange, placeholder = 'Name or ID', scope, value,
}: FilterSelectProps) => {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<FilterOption | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(input), DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [input]);

  const { isLoading, options } = useFilterOptions(kind, query, scope, open);
  // A filter that arrived in the URL has an id but no name yet: look the id up once.
  const unnamed = value !== '' && picked?.value !== value;
  const named = useFilterOptions(kind, value, scope, unnamed);
  const known = picked?.value === value ? picked : named.options.find(option => option.value === value);
  const selected = value === '' ? null : (known ?? { label: `ID ${value}`, value });
  const scoped = scope.staffer_id != null || scope.subscriber_id != null;

  return (
    <SPASelect<FilterOption>
      isClearable
      filterOption={null}
      formatOptionLabel={(option, { context }) => <OptionLabel context={context} option={option} />}
      inputId={inputId}
      inputValue={input}
      isLoading={isLoading}
      noOptionsMessage={() => EMPTY[kind](scoped)}
      options={options}
      placeholder={placeholder}
      styles={compactSelectStyles}
      value={selected}
      onChange={(option) => {
        setPicked(option);
        setInput('');
        onChange(option?.value ?? '');
      }}
      onInputChange={(text, meta) => {
        // Typed text left in the box hides the chosen name, so it goes on pick and on blur.
        if (meta.action === 'input-change') setInput(text);
        if (meta.action === 'input-blur' || meta.action === 'menu-close') setInput('');
      }}
      onMenuClose={() => setOpen(false)}
      onMenuOpen={() => setOpen(true)}
    />
  );
};

export default FilterSelect;
